import { Phone, Check, ChevronRight } from "lucide-react";
import { IMAGES, BRAND } from "@/lib/data";
import { Overline } from "@/components/Section";
import ApplyForm from "@/components/ApplyForm";
import SEOHead from "@/components/SEOHead";
import { Link } from "react-router-dom";

const APPLY_FAQS = [
  { q: "Do I pay anything to get started?", a: "Never. Drivers pay zero — no application fees, no placement fees, no startup costs. Hylas Companion is paid by the carriers we place you with." },
  { q: "Do you hold my first paycheck?", a: "No. Settlements are processed weekly with no held pay. You'll know your exact pay schedule from day one." },
  { q: "When will a recruiter call me?", a: "Our goal is to call within 1 business day of your application — usually the same day during business hours. We call. We don't ghost." },
  { q: "Do I need prior OTR experience?", a: "Not for our Trainee Program. You'll ride with a seasoned trainer to build your road hours, then move to a solo seat. CDL-A required." },
  { q: "Do you welcome female drivers?", a: "Absolutely. We recruit based on qualifications only. Every position we offer is open to all CDL-A licensed drivers equally." },
  { q: "What if I have violations or accidents?", a: "Apply anyway and be upfront. Our recruiters match you with carriers whose safety standards fit your record. We won't waste your time or theirs." },
];

const APPLY_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Apply for CDL Truck Driver Jobs | Hylas Companion",
  "url": "https://hylascompanion.com/apply",
  "description": "Apply for OTR, Flatbed, Reefer, Local, and Lease Purchase truck driver jobs at Hylas Companion. No application fee. Teams, singles, trainees — all welcome.",
  "potentialAction": {
    "@type": "ApplyAction",
    "target": "https://hylascompanion.com/apply",
    "object": {
      "@type": "JobPosting",
      "title": "CDL-A Truck Driver",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "Hylas Companion",
        "url": "https://hylascompanion.com",
      },
    },
  },
};

export default function Apply() {
  return (
    <div data-testid="apply-page">
      <SEOHead
        title="Apply for CDL Truck Driver Jobs | Hylas Companion | 2 Minutes"
        description="Apply for OTR, Flatbed, Reefer, Local W2, and Lease Purchase truck driver jobs at Hylas Companion. No fees. Teams, singles, trainees welcome. Complete your CDL-A driver application in under 2 minutes."
        keywords="apply truck driver job, CDL-A job application, OTR driver application, trucking job apply online, flatbed driver apply, reefer driver apply"
        path="/apply"
        schema={APPLY_SCHEMA}
      />
      <section className="pt-32 md:pt-40 pb-20 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-start">
          <div className="lg:sticky lg:top-28">
            <Overline>Apply Now</Overline>
            <h1 className="font-head text-4xl md:text-6xl font-bold uppercase tracking-[0.15em] mt-5 leading-tight">
              Get Rolling
            </h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-md leading-relaxed">
              Fill this out in under two minutes. Our team calls you back fast to match you with the right new truck and lane.
            </p>
            <div className="mt-8 space-y-3">
              {["Drivers never pay a dime", "New trucks, top pay per mile", "All 48 states — no hiring restriction", "Flexible home time + vacations"].map((x) => (
                <div key={x} className="flex items-center gap-3 text-foreground/80">
                  <Check className="w-5 h-5 text-primary shrink-0" /> {x}
                </div>
              ))}
            </div>
            <a href={`tel:${BRAND.phone}`} className="mt-8 inline-flex items-center gap-3 font-head uppercase tracking-[0.15em] text-[#0A0A0A] hover:text-primary transition-colors">
              <Phone className="w-5 h-5" /> {BRAND.phone}
            </a>
            <img src={IMAGES.byTrailer} alt="CDL-A truck driver by flatbed trailer — apply for Hylas Companion driver jobs" className="mt-10 w-full h-64 object-cover border border-border hidden lg:block" loading="lazy" />
          </div>

          <ApplyForm />
        </div>
      </section>

      {/* ── FAQ Strip ── */}
      <section className="border-t border-border bg-[#0A0A0A]/[0.02] py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="font-head text-[10px] uppercase tracking-[0.35em] text-primary mb-1">Before you submit</p>
              <h2 className="font-head text-2xl md:text-3xl uppercase tracking-[0.1em] text-[#0A0A0A]">Quick Answers</h2>
            </div>
            <Link
              to="/faq"
              className="flex items-center gap-1.5 font-head text-xs uppercase tracking-[0.2em] text-foreground/50 hover:text-primary transition-colors"
            >
              Full FAQ <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-border">
            {APPLY_FAQS.map((item, i) => (
              <div
                key={i}
                data-testid={`apply-faq-${i}`}
                className="p-6 border-b border-r border-border last:border-b-0 [&:nth-child(3n)]:border-r-0 [&:nth-last-child(-n+1)]:border-b-0 [&:nth-last-child(-n+2)]:sm:border-b-0 [&:nth-last-child(-n+3)]:lg:border-b-0"
              >
                <p className="font-head text-sm uppercase tracking-[0.1em] text-[#0A0A0A] leading-snug mb-3">
                  {item.q}
                </p>
                <p className="text-sm text-foreground/65 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
